import type { PropagatedAnnotation } from "@/hooks/useTaskRelatedFrames";
import type {
  FeEmptyArea,
  IdxToFeEmptyArea,
  SelectedProduct,
} from "@/store/useCoverageBetaStore";
import type { ProductSchema } from "@labeling-tools/api/atlas";
import { orderCorners } from "@labeling-tools/ui-components/src/utils/obbGeometry";
import { maxBy, values } from "lodash";

/** Overlap at which a propagated area is the same spot as an existing one. */
export const PROPAGATED_IOU_MATCH = 0.5;

type Point = [number, number];

const signedArea = (ring: Point[]) =>
  ring.reduce((sum, [x, y], i) => {
    const [nx, ny] = ring[(i + 1) % ring.length];
    return sum + x * ny - nx * y;
  }, 0) / 2;

/** `subject` clipped to the convex `clip` ring (Sutherland–Hodgman). */
const clipConvex = (subject: Point[], clip: Point[]) => {
  const side = Math.sign(signedArea(clip));
  let out = subject;
  clip.forEach((a, i) => {
    const b = clip[(i + 1) % clip.length];
    // Positive on the clip ring's inner side of edge a→b.
    const cross = (p: Point) =>
      side * ((b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]));
    const inside = (p: Point) => cross(p) >= 0;
    const crossing = (p: Point, q: Point): Point => {
      const t = cross(p) / (cross(p) - cross(q));
      return [p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])];
    };
    const input = out;
    out = [];
    input.forEach((p, j) => {
      const q = input[(j + 1) % input.length];
      if (inside(p)) {
        out.push(p);
        if (!inside(q)) out.push(crossing(p, q));
      } else if (inside(q)) {
        out.push(crossing(p, q));
      }
    });
  });
  return out;
};

/**
 * Polygon IoU of two quads. Both sides are rotated rectangles — auto
 * detections (~95% rotated) and SAM2 guesses (the tracked mask's
 * min-area rectangle) — so this compares like with like. Corners may come
 * in any order (detections are row-major); they're ringed first.
 */
export const quadIou = (a: number[][], b: number[][]) => {
  const p = orderCorners(a as Point[]);
  const q = orderCorners(b as Point[]);
  const areaP = Math.abs(signedArea(p));
  const areaQ = Math.abs(signedArea(q));
  if (!areaP || !areaQ) return 0;
  const overlap = clipConvex(p, q);
  const inter = overlap.length < 3 ? 0 : Math.abs(signedArea(overlap));
  return inter / (areaP + areaQ - inter);
};

/** Untouched by the labeler: nothing picked, not reviewed, not deleted. */
const isUntouched = (area: FeEmptyArea) =>
  !area.isDeleted &&
  !area.isValid &&
  !area.propagated &&
  area.selectedProducts.length === 0;

/**
 * Pre-fill a task's areas from annotations propagated onto its primary parcel.
 *
 * Each annotation (several source tasks may cover the same spot — the
 * highest-scoring one wins) either pre-selects its products on the untouched
 * auto detection it overlaps, or is added as a new box. Spots the labeler has
 * already worked on are left alone, and re-running with the same annotations
 * changes nothing, so this is safe to call as more annotations arrive.
 *
 * Returns the new areas plus the indices it added, or null when nothing changed.
 */
export const mergePropagatedAnnotations = (
  idxToFeEmptyArea: IdxToFeEmptyArea,
  annotations: PropagatedAnnotation[],
  productsById: Map<string, ProductSchema>,
  planogramId: string,
): { idxToFeEmptyArea: IdxToFeEmptyArea; addedIdxs: number[] } | null => {
  const next: IdxToFeEmptyArea = { ...idxToFeEmptyArea };
  const addedIdxs: number[] = [];
  const isApplied = (a: PropagatedAnnotation) =>
    values(next).some(
      (area) =>
        area.propagated?.sourceTaskId === a.source_task_id &&
        area.propagated.areaId === a.area_id,
    );

  const toProducts = (ids: string[]): SelectedProduct[] =>
    ids.flatMap((id) => {
      const product = productsById.get(id);
      return product
        ? [
            {
              ...product,
              source: "planogram" as const,
              planogram_id: planogramId,
            },
          ]
        : [];
    });

  [...annotations]
    .sort((a, b) => b.score - a.score)
    .forEach((annotation) => {
      if (isApplied(annotation)) return;
      const overlapping = values(next).filter(
        (area) =>
          !area.isDeleted &&
          quadIou(area.points, annotation.points) >= PROPAGATED_IOU_MATCH,
      );
      // Already handled — by the labeler, or by a higher-scoring annotation.
      if (overlapping.some((area) => !isUntouched(area))) return;

      const origin = {
        sourceTaskId: annotation.source_task_id,
        areaId: annotation.area_id,
        productIds: annotation.product_ids,
      };
      // Near-duplicate detections can both clear the threshold; the products
      // go on the closest one.
      const match = maxBy(overlapping, (area) =>
        quadIou(area.points, annotation.points),
      );
      if (match) {
        next[match.feEmptyAreaIdx] = {
          ...match,
          selectedProducts: toProducts(annotation.product_ids),
          propagated: { ...origin, points: match.points },
        };
        return;
      }

      const keys = Object.keys(next).map(Number);
      const idx = keys.length ? Math.max(...keys) + 1 : 0;
      next[idx] = {
        id: crypto.randomUUID(),
        points: annotation.points,
        class_index: 0,
        confidence: annotation.score,
        source: "human",
        feEmptyAreaIdx: idx,
        isDeleted: false,
        selectedProducts: toProducts(annotation.product_ids),
        isHidden: false,
        isValid: false,
        propagated: { ...origin, points: annotation.points },
      };
      addedIdxs.push(idx);
    });

  const changed = values(next).some(
    (area) => area !== idxToFeEmptyArea[area.feEmptyAreaIdx],
  );
  return changed ? { idxToFeEmptyArea: next, addedIdxs } : null;
};
